/**
 * Обёртка над jscanify — детект 4 углов листа и перспективная коррекция.
 *
 * Lazy-load: jscanify тянет OpenCV.js (~10 MB WASM). Грузим только при первом
 * вызове сканера — после клика «Снять», не на загрузке Mini App.
 *
 * Two-path flow (как у Adobe Scan):
 * 1. detectCorners(canvas) — быстрый автоматический детект.
 *    - Нашёл → warpWithCorners(canvas, corners) сразу делает warp.
 *    - Не нашёл → caller показывает ManualCornerEditor.vue,
 *      пользователь перетаскивает 4 точки, потом warpWithCorners передаются
 *      исправленные углы.
 * 2. Финальный JPEG → downscale 2000px + quality 85.
 */

const MAX_LONG_SIDE = 2000;
const JPEG_QUALITY = 0.85;
const A4_W = 1240; // ~150 DPI на A4
const A4_H = 1754;

export interface Corner {
  x: number;
  y: number;
}

export interface DetectedCorners {
  topLeftCorner: Corner;
  topRightCorner: Corner;
  bottomRightCorner: Corner;
  bottomLeftCorner: Corner;
}

type JScanifyInstance = {
  getCornerPoints(canvas: HTMLCanvasElement): DetectedCorners | null;
  extractPaper(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    cornerPoints?: DetectedCorners
  ): HTMLCanvasElement;
};

let _scanner: JScanifyInstance | null = null;
let _loadingPromise: Promise<JScanifyInstance> | null = null;

type LoadListener = (state: 'start' | 'done' | 'error') => void;
const _loadListeners = new Set<LoadListener>();

export function onScannerLoad(listener: LoadListener): () => void {
  _loadListeners.add(listener);
  return () => _loadListeners.delete(listener);
}

function notify(state: 'start' | 'done' | 'error'): void {
  for (const l of _loadListeners) {
    try {
      l(state);
    } catch {
      // ignore
    }
  }
}

export function isScannerReady(): boolean {
  return _scanner !== null;
}

async function ensureScanner(): Promise<JScanifyInstance> {
  if (_scanner !== null) return _scanner;
  if (_loadingPromise !== null) return _loadingPromise;

  notify('start');
  _loadingPromise = (async () => {
    try {
      // @ts-expect-error — jscanify не имеет типов
      const mod = await import('jscanify');
      const JScanify = (mod.default || mod) as new () => JScanifyInstance;
      const inst = new JScanify();
      _scanner = inst;
      notify('done');
      return inst;
    } catch (err) {
      notify('error');
      _loadingPromise = null;  // позволить retry
      throw err;
    }
  })();
  return _loadingPromise;
}

/**
 * Префетч jscanify в фоне после загрузки страницы.
 * Не блокирует UI; если уже загружено — no-op.
 */
export function prefetchScanner(): void {
  void ensureScanner().catch(() => {
    // тихо игнорируем — fallback на input/capture есть
  });
}

/**
 * Публичный доступ к инстансу jscanify (для live-scanner.ts).
 * Гарантирует что после await jscanify+OpenCV.js полностью загружены.
 */
export async function ensureScannerInstance(): Promise<JScanifyInstance> {
  return ensureScanner();
}

/**
 * Попытаться автоматически найти 4 угла листа.
 * Возвращает null если jscanify не справился (плохой контраст, мятый лист и т.п.).
 */
export async function detectCorners(
  sourceCanvas: HTMLCanvasElement
): Promise<DetectedCorners | null> {
  try {
    const scanner = await ensureScanner();
    return scanner.getCornerPoints(sourceCanvas);
  } catch (err) {
    console.warn('scanner.detect_failed', err);
    return null;
  }
}

/**
 * Сделать warp с переданными углами (либо автоматическими, либо от
 * ManualCornerEditor) и вернуть JPEG-Blob готовый к pdf-lib (embedJpg).
 */
export async function warpWithCorners(
  sourceCanvas: HTMLCanvasElement,
  corners: DetectedCorners
): Promise<Blob> {
  let warped: HTMLCanvasElement | null = null;
  try {
    const scanner = await ensureScanner();
    warped = scanner.extractPaper(sourceCanvas, A4_W, A4_H, corners);
  } catch (err) {
    console.warn('scanner.warp_failed — fallback to raw downscale', err);
  }

  const finalCanvas = warped ?? downscale(sourceCanvas, MAX_LONG_SIDE);
  return canvasToJpegBlob(finalCanvas, JPEG_QUALITY);
}

/**
 * Удобство: вызвать detect + warp одним шагом.
 * Возвращает { jpeg, autoDetected }:
 * - autoDetected=true — углы нашлись автоматически.
 * - autoDetected=false — fallback на даунскейл (caller может предложить ручную коррекцию).
 */
export async function autoExtractAndWarp(
  sourceCanvas: HTMLCanvasElement
): Promise<{ jpeg: Blob; autoDetected: boolean }> {
  const corners = await detectCorners(sourceCanvas);
  if (corners) {
    const jpeg = await warpWithCorners(sourceCanvas, corners);
    return { jpeg, autoDetected: true };
  }
  const fallback = downscale(sourceCanvas, MAX_LONG_SIDE);
  const jpeg = await canvasToJpegBlob(fallback, JPEG_QUALITY);
  return { jpeg, autoDetected: false };
}

/**
 * Просто JPEG из canvas без детекта углов.
 */
export function captureRawJpeg(sourceCanvas: HTMLCanvasElement): Promise<Blob> {
  const ds = downscale(sourceCanvas, MAX_LONG_SIDE);
  return canvasToJpegBlob(ds, JPEG_QUALITY);
}

// ─── Утилиты ───────────────────────────────────────────────────────────────

function downscale(src: HTMLCanvasElement, maxLong: number): HTMLCanvasElement {
  const long = Math.max(src.width, src.height);
  if (long <= maxLong) return src;

  const ratio = maxLong / long;
  const dst = document.createElement('canvas');
  dst.width = Math.round(src.width * ratio);
  dst.height = Math.round(src.height * ratio);
  const ctx = dst.getContext('2d');
  if (!ctx) return src;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, dst.width, dst.height);
  return dst;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      },
      'image/jpeg',
      quality
    );
  });
}
